import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useFormatting } from '../../../locale';
import { useClientsSchema } from '../provider/ClientsSchemaProvider';
import { useClientList } from '../../../hooks/useClientList';
import { ClientSchemaManager } from '../utils/ClientSchemaManager';
import { adaptClientList } from '../adapters/adaptClientList';
import Preloader from '../../../components/Preloader';
import { COUNT_OF_PAGE } from '../constants';
import { useOptionList } from '../../../hooks/useOptionList';
import { OPTION_LIST } from '../../../constants/constants';

type Sort = {
    key: string;
    direction: string;
};

type Filter = {
    key: string;
    value: unknown[];
};

type SourceColumns = ({ t }: { t: (key: string) => string }) => Array<Record<string, unknown>>;

type HocProps = {
    search?: string;
    selection?: 'radio' | 'checkbox';
    externalFilters?: unknown;
    [key: string]: unknown;
};

const withClientTable = (
    Component: React.ComponentType<Record<string, unknown>>,
    isProspects: boolean,
    sourceColumns: SourceColumns,
) => ({
    search = '',
    selection,
    externalFilters,
    ...props
}: HocProps) => {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [filters, setFilters] = useState<Filter[]>([]);
    const [sort, setSort] = useState<Sort>({
        key: '',
        direction: 'asc',
    });
    const {
        getFormattedDate,
    } = useFormatting();
    const {
        schemasColumns: {
            data: schemasData,
        },
        schemasLists,
    } = useClientsSchema();
    const { t } = useTranslation();
    const {
        isLoading: isOptionListLoading,
        lists: [optionGender, optionLanguage, optionCountry],
    } = useOptionList([OPTION_LIST.gender, OPTION_LIST.language, OPTION_LIST.country]);

    const columns = useMemo(
        () => (
            ClientSchemaManager.generateColumns({
                dataList: schemasLists?.data as Array<Record<string, unknown>>,
                columns: sourceColumns({ t }),
                fields: (schemasData as { fields?: Array<Record<string, unknown>> })?.fields,
            })
        ),
        [schemasLists, schemasData, sourceColumns, t],
    );

    const onChangeSort = ({
        key,
        direction,
    }: Sort) => {
        setSort(key
            ? {
                key,
                direction,
            }
            : { key: '', direction: 'asc' });
    };

    const order = useMemo(() => (
        ClientSchemaManager.generateSortValue({
            key: sort.key,
            direction: sort?.direction,
            columns: columns as Array<{ key: string; dataPath?: string; fieldType?: string; isArrayItem?: boolean }>,
        })
    ), [sort, columns]);

    const vars = useMemo(
        () => ({
            includeTotalCount: true,
            pageNumber: currentPage,
            pageSize: COUNT_OF_PAGE,
            search,
            order,
        }),
        [currentPage, search, order],
    );

    const {
        data = [],
        isLoading,
        totalCount,
        error,
    } = useClientList(
        vars,
        externalFilters,
    );

    useEffect(() => {
        if (search) {
            setCurrentPage(1);
        }
    }, [search]);

    const onFilter = (filter: Filter) => {
        const uniqFilters = filters.filter(
            ({ key }) => key !== filter.key,
        );

        if (filter.value.length > 0) {
            setFilters([...uniqFilters, filter]);
        } else {
            setFilters(uniqFilters);
        }
    };

    const adaptedData = adaptClientList({
        navigate: (path, state) => navigate(path, { state }),
        t,
        data: data as Record<string, unknown>[],
        optionGender: optionGender as Array<{ id: string | number; label: string }>,
        optionLanguage: optionLanguage as Array<{ id: string | number; label: string }>,
        optionCountry: optionCountry as Array<{ id: string | number; label: string }>,
        columns: columns as Array<{ key: string; render?: unknown }>,
        isLoading: isLoading || isOptionListLoading,
        getFormattedDate,
        pageSize: COUNT_OF_PAGE,
    });

    return (
        <Preloader isLoading={false} error={error}>
            <Component
                data={adaptedData}
                columns={columns}
                sort={sort}
                isLoading={isLoading || isOptionListLoading}
                paging={{
                    totalCount,
                    currentPage,
                    pageSize: COUNT_OF_PAGE,
                }}
                onChangePagination={setCurrentPage}
                onChangeFilter={onFilter}
                onChangeSort={onChangeSort}
                selection={selection}
                isProspects={isProspects}
                {...props}
            />
        </Preloader>
    );
};

export default withClientTable;
